/**
 * Marker.io feedback widget loader.
 *
 * Loaded for every authenticated user — gating on login (not an allowlist)
 * keeps random visitors out while avoiding the "my email isn't on the list"
 * class of bugs. Tickets arrive pre-tagged with the reporter's identity
 * and tenant/route metadata so triage is still unambiguous.
 *
 * Privacy: network recording drops every credential/session/two-step key
 * (``MARKER_EXCLUDED_NETWORK_KEYS``) and the upload bucket's domain
 * (``MARKER_EXCLUDED_NETWORK_DOMAINS``), secrets on screen carry
 * ``MARKER_EXCLUDE_CLASS``, and the widget is UNLOADED (not hidden) on logout
 * and before a different account's session, so nothing recorded under one
 * sign-in rides into the next. Marker's network recording is a paid-plan
 * feature and is off on our plan today (AW-126 ticket context shows
 * networkRequests as "Requires a plan upgrade"); the redaction is defence in
 * depth for the day it is turned on.
 */

import markerSDK, { type MarkerSdk } from '@marker.io/browser';

import { config } from '@/config/environment';

import { MARKER_EXCLUDED_NETWORK_DOMAINS, MARKER_EXCLUDED_NETWORK_KEYS } from './markerPrivacy';

export interface MarkerReporter {
  email: string;
  fullName: string;
}

export interface MarkerCustomData {
  tenant_id?: string;
  tenant_slug?: string;
  user_id?: string;
  role?: string;
  environment?: string;
  route?: string;
}

let widgetPromise: Promise<MarkerSdk | null> | null = null;
/** The account (tenant + user) the current widget was loaded for. */
let loadedFor: string | null = null;

/**
 * Load the Marker widget once per signed-in account. Subsequent calls for the
 * same account return the same instance; a different account unloads the
 * previous widget first. Returns null if no project ID is configured.
 */
export async function loadMarkerWidget(
  reporter: MarkerReporter,
  customData: MarkerCustomData = {},
): Promise<MarkerSdk | null> {
  if (!config.MARKER_PROJECT_ID) {
    return null;
  }

  const account = `${customData.tenant_id ?? ''}:${customData.user_id ?? reporter.email}`;
  // Loop: another call may have started a load for someone else while this
  // one waited on an unload.
  while (widgetPromise && loadedFor !== account) {
    await unloadMarkerWidget();
  }

  if (!widgetPromise) {
    loadedFor = account;
    const load: Promise<MarkerSdk | null> = markerSDK
      .loadWidget({
        project: config.MARKER_PROJECT_ID,
        networkRecording: {
          excludedKeys: [...MARKER_EXCLUDED_NETWORK_KEYS],
          excludedDomains: [...MARKER_EXCLUDED_NETWORK_DOMAINS],
        },
      })
      .catch((err) => {
        // Don't let Marker failures break the app — just log and return null.
        console.warn('[marker.io] Failed to load widget', err);
        // Only this load's own state: a stale failure (it was already
        // unloaded and a newer load started) must not erase the newer widget,
        // which would then never be unloaded on sign-out.
        if (widgetPromise === load) {
          widgetPromise = null;
          loadedFor = null;
        }
        return null;
      });
    widgetPromise = load;
  }

  const pending = widgetPromise;
  const widget = await pending;
  // Unloaded (a logout, an account switch) while it was loading.
  if (!widget || widgetPromise !== pending) return null;

  widget.setReporter({ email: reporter.email, fullName: reporter.fullName });
  const stringified: Record<string, string> = { environment: config.ENVIRONMENT };
  for (const [k, v] of Object.entries(customData)) {
    if (v !== undefined && v !== null) stringified[k] = String(v);
  }
  widget.setCustomData(stringified);

  return widget;
}

/**
 * Unload the widget and every resource it holds (logout, account switch).
 * The next sign-in loads a fresh one.
 */
export async function unloadMarkerWidget(): Promise<void> {
  // Detach this load first, then wait for exactly it and unload what it gave;
  // a load started after this point is a different widget, left alone.
  const pending = widgetPromise;
  widgetPromise = null;
  loadedFor = null;
  if (!pending) return;
  const widget = await pending;
  try {
    widget?.unload();
  } catch (err) {
    console.warn('[marker.io] Failed to unload widget', err);
  }
}
